import { z } from 'zod'
import { api } from '@/shared/api'

const billingPriceSummarySchema = z.object({
  period: z.number(),
  price: z.number(),
  name: z.string(),
  url: z.string(),
  priceId: z.string(),
})

const billingPromoSummarySchema = z.object({
  promoCode: z.string(),
  promoPeriodsUsed: z.number().nullable(),
  promoPeriodsTotal: z.number().nullable(),
  discountDisplayedPercent: z.number().nullable(),
  discountMultiplier: z.number().nullable(),
})

const billingSummarySchema = z.object({
  status: z.string().nullable(),
  tariff: z.string().nullable(),
  nextTransactionAt: z.string().datetime({ offset: true }).nullable(),
  currentPrice: billingPriceSummarySchema.nullable(),
  nextPrice: billingPriceSummarySchema.nullable(),
  promo: billingPromoSummarySchema.nullable(),
  nextChargeAmountCents: z.number().nullable(),
  linkedCardAvailable: z.boolean(),
  linkedCardLastFour: z.string().nullable(),
  snapshotSource: z.string().nullable(),
  snapshotUpdatedAt: z.string().datetime({ offset: true }).nullable(),
})

const billingSummaryResponseSchema = z.object({
  summary: billingSummarySchema.nullable(),
})

const billingHistoryItemSchema = z.object({
  operationId: z.string().nullable(),
  bubbleServiceId: z.string().nullable(),
  amountCents: z.number().nullable(),
  currency: z.string().nullable(),
  declined: z.boolean(),
  description: z.string().nullable(),
  paidAt: z.string().datetime({ offset: true }).nullable(),
})

const billingHistoryResponseSchema = z.object({
  payments: z.array(billingHistoryItemSchema),
})

export type BillingPriceSummary = z.infer<typeof billingPriceSummarySchema>
export type BillingPromoSummary = z.infer<typeof billingPromoSummarySchema>
export type BillingSummaryDto = z.infer<typeof billingSummarySchema>
export type BillingHistoryItemDto = z.infer<typeof billingHistoryItemSchema>

export type BillingSummary = Omit<BillingSummaryDto, 'nextTransactionAt' | 'snapshotUpdatedAt'> & {
  nextTransactionAt: Date | null
  snapshotUpdatedAt: Date | null
}

export type BillingHistoryItem = Omit<BillingHistoryItemDto, 'paidAt'> & {
  paidAt: Date | null
}

const normalizeBillingSummary = (summary: BillingSummaryDto | null): BillingSummary | null => {
  if (!summary) {
    return null
  }

  return {
    ...summary,
    nextTransactionAt: summary.nextTransactionAt ? new Date(summary.nextTransactionAt) : null,
    snapshotUpdatedAt: summary.snapshotUpdatedAt ? new Date(summary.snapshotUpdatedAt) : null,
  }
}

const normalizeBillingHistoryItem = (item: BillingHistoryItemDto): BillingHistoryItem => ({
  ...item,
  paidAt: item.paidAt ? new Date(item.paidAt) : null,
})

export async function loadBillingSummary() {
  const response = await api.get<unknown>('/api/v1/payments/summary')
  const parsed = billingSummaryResponseSchema.parse(response)
  return normalizeBillingSummary(parsed.summary)
}

export async function loadBillingHistory() {
  const response = await api.get<unknown>('/api/v1/payments/history')
  const parsed = billingHistoryResponseSchema.parse(response)
  return parsed.payments.map(normalizeBillingHistoryItem)
}
